import { Box, Typography, Stack, Autocomplete, TextField, Paper } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { Footer } from '@/components/details/Footer';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import DetailField from '@/components/common/DetailField';
import BasicActivityInfo from '@/components/details/BasicActivityInfo';
import {
  CharacterizationTechniqueDto,
  PrivilegeEnum,
  SampleDto,
  StatusDto,
  UpdateSampleActivityRequest,
  SampleActivityDto,
  CharacterizationInstrumentDto,
} from '@/api/ApiClient';
import {
  formatCharacterizationInstrumentLabel,
  getCharacterizationInstrumentsForFacility,
} from '@/lib/utils/characterizationInstruments';
import { getCharacterizationFacilityChoicesForSample } from '@/lib/utils/sampleActivityLifecycleEligibility';
import InlineImages from '@/components/common/InlineImages';

import { useBackend } from '@/hooks/useBackend';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useSampleActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { log } from '@/lib/utils/logger';
import { updateSampleActivityWithPreconditionRetry } from '@/lib/tree/submitEntityUtils';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createCharacterizationActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

import { nodeTypeIsReadonly } from '@/lib/tree/treeNodeTypeConfigs';

const OPTION_KEYS: OptionKey[] = [
  'researcherOptions',
  'facultyOptions',
  'status',
  'characterizationInstruments',
  'characterizationTechniques',
  'characterizationDropdownChoices',
  'visibility',
];

// Facility-specific form components
interface InternalFacilityFormProps {
  activity: SampleActivityDto;
  instrumentOptions: CharacterizationInstrumentDto[];
  isReadOnly: boolean;
  isSubmitted: boolean;
  onModification: (modified: Partial<SampleActivityDto>) => void;
}

const InternalFacilityForm: FC<InternalFacilityFormProps> = ({
  activity,
  instrumentOptions,
  isReadOnly,
  isSubmitted,
  onModification,
}) => {
  const selectedInstrument =
    instrumentOptions.find((opt) => opt.id === activity?.instrument) ??
    (activity?.instrument ? { id: activity.instrument, name: activity.instrument } : null);

  return (
    <DetailField
      label="Instrument"
      value={
        selectedInstrument ? formatCharacterizationInstrumentLabel(selectedInstrument) : activity.instrument || 'N/A'
      }
      isReadOnly={isReadOnly || isSubmitted}
      required
    >
      <Autocomplete
        options={instrumentOptions}
        getOptionLabel={(option) => formatCharacterizationInstrumentLabel(option)}
        value={selectedInstrument}
        onChange={(_, newValue) => {
          onModification({ instrument: newValue?.id ?? undefined });
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} size="small" fullWidth required />}
        sx={{ flex: 1 }}
        disabled={!activity.facility}
      />
    </DetailField>
  );
};

interface ExternalFacilityFormProps {
  activity: SampleActivityDto;
  techniqueOptions: CharacterizationTechniqueDto[];
  isReadOnly: boolean;
  isSubmitted: boolean;
  onModification: (modified: Partial<SampleActivityDto>) => void;
}

const ExternalFacilityForm: FC<ExternalFacilityFormProps> = ({
  activity,
  techniqueOptions,
  isReadOnly,
  isSubmitted,
  onModification,
}) => (
  <>
    <DetailField
      label="Characterization Technique"
      value={techniqueOptions?.find((opt) => opt.id === activity?.characterizationTechnique)?.name || 'N/A'}
      isReadOnly={isReadOnly || isSubmitted}
    >
      <Autocomplete
        options={techniqueOptions ?? []}
        getOptionLabel={(option) => option.name || ''}
        value={techniqueOptions?.find((opt) => opt.id === activity?.characterizationTechnique) || null}
        onChange={(_, newValue) => {
          onModification({ characterizationTechnique: newValue?.id ?? undefined });
        }}
        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        sx={{ flex: 1 }}
      />
    </DetailField>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      <DetailField label="Instrument" value={activity?.desc || 'N/A'} isReadOnly={isReadOnly || isSubmitted} required>
        <TextField
          fullWidth
          value={activity?.desc || ''}
          onChange={(e) => {
            onModification({ desc: e.target?.value ?? undefined });
          }}
          size="small"
          sx={{ flex: 1 }}
        />
      </DetailField>
      <DetailField
        label="Instrument DOI"
        value={activity?.instrumentDOI || 'N/A'}
        isReadOnly={isReadOnly || isSubmitted}
      >
        <TextField
          fullWidth
          value={activity?.instrumentDOI || ''}
          onChange={(e) => {
            onModification({ instrumentDOI: e.target?.value ?? undefined });
          }}
          size="small"
          sx={{ flex: 1 }}
        />
      </DetailField>
    </Box>
  </>
);

const CharacterizationActivityDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const { resolveUser } = useUserResolution();
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();

  const backend = useBackend();
  const { setDataModified, updateDataPartial, updateData, getNodeData, getSampleData } = useTree();
  const { selectedNode } = useTreeSelection();

  // Check if this is a readonly node type
  const isReadOnlyNode = selectedNode ? nodeTypeIsReadonly(selectedNode.type) : false;
  const activity = useSampleActivityData(selectedNode);

  /** Owning sample for lifecycle rules (walk parents until a sample node). */
  const ownerSample = useMemo((): SampleDto | null => {
    if (!selectedNode?.parentId) {
      return null;
    }
    let nodeId: string | undefined = selectedNode.parentId;
    for (let depth = 0; depth < 16 && nodeId; depth++) {
      const node = getNodeData(nodeId);
      if (!node) {
        return null;
      }
      if (
        node.type === 'sample' ||
        node.type === 'sampleAggregate' ||
        node.type === 'sampleSnapshot' ||
        node.type === 'sampleInPackage'
      ) {
        return getSampleData(node);
      }
      nodeId = node.parentId;
    }
    return null;
  }, [getNodeData, getSampleData, selectedNode]);

  // Computed values
  const isExternalFacility = activity?.facility === 'External';
  const characterizationFacilityChoices = useMemo(
    () =>
      getCharacterizationFacilityChoicesForSample(
        ownerSample ?? undefined,
        options.characterizationDropdownChoices?.facilities,
        { currentFacility: activity?.facility }
      ),
    [ownerSample, activity?.facility, options.characterizationDropdownChoices?.facilities]
  );

  const instrumentOptions = useMemo(
    () =>
      getCharacterizationInstrumentsForFacility(
        options.characterizationInstruments ?? [],
        isExternalFacility ? undefined : activity?.facility,
        activity?.instrument
      ),
    [activity?.facility, activity?.instrument, isExternalFacility, options.characterizationInstruments]
  );

  // Generate image title based on technique
  const imageTitle = useMemo(() => {
    let technique: CharacterizationTechniqueDto | undefined;
    if (isExternalFacility) {
      technique = options.characterizationTechniques?.find((t) => t.id === activity?.characterizationTechnique);
    } else {
      const instrument = options.characterizationInstruments?.find((i) => i.id === activity?.instrument);
      technique = options.characterizationTechniques?.find((t) => t.id === instrument?.characterizationTechnique);
    }
    return technique ? `${technique.name} Images` : 'Analysis Images';
  }, [
    isExternalFacility,
    options.characterizationTechniques,
    options.characterizationInstruments,
    activity?.characterizationTechnique,
    activity?.instrument,
  ]);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  // Centralized date handling utility
  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? new Date(date.toDateString()) : undefined;
  };

  const isSubmitted = activity?.status === StatusDto.Submitted;

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  if (!activity || !selectedNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!activity ? 'Select a node to view details' : 'Selected node is not a sample activity.'}
        </Typography>
      </Box>
    );
  }

  const handleSave = async () => {
    if (!activity?.id) return;
    const saveValidation = validateRequiredToSave(createCharacterizationActivitySaveValidation(activity));
    if (!saveValidation.isValid) {
      showSnackbar('Please fill in all required fields.', 'error');
      return;
    }
    try {
      const request: UpdateSampleActivityRequest = { ...activity };
      const preconditionDeps = { showSnackbar, showConfirmDialog };
      const updatedData = await updateSampleActivityWithPreconditionRetry(
        backend,
        activity.id,
        request,
        preconditionDeps
      );
      if (updatedData === null) return;
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      showSnackbar('Characterization activity saved successfully', 'success');
    } catch (error) {
      log.error('[CharacterizationActivity] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save characterization activity.', 'error');
    }
  };

  const handleModification = (modified: Partial<SampleActivityDto>) => {
    updateDataPartial(selectedNode.dataId, modified);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={3}>
          <BasicActivityInfo
            activity={activity}
            isReadOnly={isReadOnly}
            isSubmitted={isSubmitted}
            handleModification={handleModification}
            convertDateForBackend={convertDateForBackend}
            hideVisibility={isReadOnlyNode}
          />
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {/* Facility */}
              <DetailField
                label="Facility"
                value={activity.facility || 'N/A'}
                isReadOnly={isReadOnly || isSubmitted}
                required
              >
                <Autocomplete
                  options={characterizationFacilityChoices}
                  value={activity.facility ?? ''}
                  onChange={(_, newValue) => {
                    const nextFacility = newValue ?? undefined;
                    const facilityChanged = nextFacility !== activity.facility;
                    handleModification({
                      facility: nextFacility,
                      ...(facilityChanged ? { instrument: undefined } : {}),
                    });
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  sx={{ flex: 1 }}
                  disabled={Boolean(ownerSample?.shipped === true && activity.facility === 'External')}
                />
              </DetailField>

              {/* Instrument/Technique Selection */}
              {isExternalFacility ? (
                <ExternalFacilityForm
                  activity={activity}
                  techniqueOptions={options.characterizationTechniques ?? []}
                  isReadOnly={isReadOnly}
                  isSubmitted={isSubmitted}
                  onModification={handleModification}
                />
              ) : activity.facility ? (
                <InternalFacilityForm
                  activity={activity}
                  instrumentOptions={instrumentOptions}
                  isReadOnly={isReadOnly}
                  isSubmitted={isSubmitted}
                  onModification={handleModification}
                />
              ) : null}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {/* Runtime */}
              <DetailField label="Runtime (h)" value={activity.runtime ?? 'N/A'} isReadOnly={isReadOnly}>
                <TextField
                  type="number"
                  value={activity.runtime ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleModification({ runtime: value ? parseFloat(value) : undefined });
                  }}
                  size="small"
                  fullWidth
                />
              </DetailField>

              {/* Characterization ID */}
              {activity?.charId ? (
                <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Characterization ID
                  </Typography>
                  <Typography variant="body1">{activity.charId}</Typography>
                </Box>
              ) : null}
            </Box>
          </Paper>

          {/* Description */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Description:
            </Typography>
            {isReadOnly || isSubmitted ? (
              activity.desc ? (
                <Typography variant="body1">
                  <span dangerouslySetInnerHTML={{ __html: activity.desc }} />
                </Typography>
              ) : (
                <Typography variant="body1">N/A</Typography>
              )
            ) : (
              <RichTextEditor
                value={activity.desc ?? ''}
                onChange={(content) => handleModification({ desc: content })}
              />
            )}
          </Box>

          {/* Image Section */}
          <InlineImages title={imageTitle} />
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        {isReadOnlyNode ? (
          <ReadOnlyFooter
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={activity.lastChangedByUser || 'Unknown'}
            status={activity.status || 'Draft'}
          />
        ) : (
          <Footer
            lastChangedDate={activity.lastChangedDate}
            lastChangedBy={resolveUser(activity.lastChangedByUser || '')}
            status={activity.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={(val) => setIsManuallyEditing(!val)}
            onSave={handleSave}
            privilege={PrivilegeEnum.CharacterizationActivity}
          />
        )}
      </Box>
    </Box>
  );
};

export default CharacterizationActivityDetails;
